'use client';

import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControlLabel,
  IconButton,
  Slider,
  Switch,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CustomSelectField from '../form/CustomSelectField';
import {
  geminiModel,
  getGeminiModelPriceLabel,
  GEMINI_MODEL_DEFAULT,
  structuredEngineType,
  GPT4O_PRICE_LABEL,
  geminiSamplingParam,
  geminiThinkingSlider,
  getGeminiThinkingType,
  sliderValueToThinking,
  thinkingToSliderValue,
  SUB_PATTERN_ID_BASE,
} from '../../../constants/enum';
import { gray40, gray80, gray100, white, orange50, blue50 } from '../../../constants/colors';
import {
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import type { SubPatternType } from '../../../types/subPattern';
import SubPatternReOcrSelect from '../parts/SubPatternReOcrSelect';

// 再読取モーダルで選べる検証設定（#2960）。
// OCRエンジンは初回のAzureキャッシュを使うため変更不可。値取得モデル・構造化・パラメータのみ。
export interface ReOcrSettings {
  geminiModel: number;
  structuredEngine: number;
  includeCoordinates: boolean;
  geminiTemperature: number;
  geminiTopP: number;
  thinkingBudget: number | null;
  thinkingLevel: string | null;
}

type Props = {
  open: boolean;
  onClose: () => void;
  settings: ReOcrSettings;
  onChange: (settings: ReOcrSettings) => void;
  /** 選ばれた子パターンで読み直す。子パターンの選択欄が無い場合は undefined */
  onExecute: (subPatternId?: string) => void;
  // OCR枚数消費の案内文（青文字で表示。アップロード時と同じトーン）
  creditInfoText?: string;
  /** 選択肢に出す子パターン（#2967）。空なら選択欄を出さない */
  subPatterns?: SubPatternType[];
  /** 現在適用されている子パターンID。初期選択に使う */
  currentSubPatternId?: string | null;
};

const sliderStyle = {
  color: orange50,
  '& .MuiSlider-thumb': { backgroundColor: orange50 },
  '& .MuiSlider-track': { backgroundColor: orange50, borderColor: orange50 },
};

const ReOcrSettingsModal = ({
  open,
  onClose,
  settings,
  onChange,
  onExecute,
  creditInfoText,
  subPatterns = [],
  currentSubPatternId,
}: Props): ReactElement => {
  const set = (patch: Partial<ReOcrSettings>): void => onChange({ ...settings, ...patch });

  // 選択中の子パターン。'base' は子パターンを使わず読取パターンの設定で読む
  const [selectedLayout, setSelectedLayout] = useState<string>(SUB_PATTERN_ID_BASE);

  // 開くたびに現在の状態へ戻す。前回の選択が残っていると、
  // 「今どの子パターンで読み直そうとしているか」を取り違える
  useEffect(() => {
    if (!open) return;
    setSelectedLayout(currentSubPatternId ?? SUB_PATTERN_ID_BASE);
  }, [open, currentSubPatternId]);

  const showLayoutSelect = subPatterns.length > 0;

  const thinkingSliderValue = thinkingToSliderValue(
    settings.geminiModel,
    settings.thinkingBudget,
    settings.thinkingLevel,
  );
  const isThinkingDisabled = getGeminiThinkingType(settings.geminiModel) === 'none';

  const sectionLabel = { color: gray100, fontSize: '14px', marginBottom: '8px' };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ '& .MuiPaper-root': { width: '480px', maxWidth: '90vw', padding: 0 } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px 8px',
        }}
      >
        <Typography sx={{ fontSize: '18px', color: gray100 }}>再読み取りの設定</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ padding: '8px 24px 24px' }}>
        <Typography sx={{ color: gray80, fontSize: '13px', marginBottom: '16px' }}>
          OCRエンジンは初回の読み取り結果を再利用します（変更不可）。値取得・構造化・パラメータを変えて読み直せます。
        </Typography>

        {/* 値取得エンジン（モデル） */}
        <Box sx={{ marginBottom: '16px' }}>
          <Typography sx={sectionLabel}>値取得エンジン</Typography>
          <CustomSelectField
            value={settings.geminiModel}
            onChange={(e): void => set({ geminiModel: Number(e.target.value) })}
            labelText=""
            requiredText=""
            list={[
              {
                option: 'Gemini 3.5 Flash-Lite（デフォルト）',
                description: getGeminiModelPriceLabel(GEMINI_MODEL_DEFAULT),
                value: GEMINI_MODEL_DEFAULT,
              },
              {
                option: 'Gemini 2.5 Pro',
                description: getGeminiModelPriceLabel(geminiModel.pro_2_5),
                value: geminiModel.pro_2_5,
                divider: true,
              },
              {
                option: 'Gemini 2.5 Flash（廃止予定）',
                description: getGeminiModelPriceLabel(geminiModel.flash_2_5),
                value: geminiModel.flash_2_5,
              },
              {
                option: 'Gemini 3 Flash',
                description: getGeminiModelPriceLabel(geminiModel.flash_3),
                value: geminiModel.flash_3,
              },
              {
                option: 'Gemini 3.1 Flash-Lite',
                description: getGeminiModelPriceLabel(geminiModel.flash_lite_3_1),
                value: geminiModel.flash_lite_3_1,
              },
              {
                option: 'Gemini 3.1 Pro',
                description: getGeminiModelPriceLabel(geminiModel.pro_3),
                value: geminiModel.pro_3,
              },
              {
                option: 'Gemini 3.5 Flash',
                description: getGeminiModelPriceLabel(geminiModel.flash_3_5),
                value: geminiModel.flash_3_5,
              },
            ]}
          />
          {/* パラメータ（座標 / 思考量 / temp / topP） */}
          <Box sx={{ paddingX: '8px', paddingTop: '8px' }}>
            {/* 座標付与（ON/OFF スイッチ。ラベル左・トグル右・オレンジ） */}
            <FormControlLabel
              labelPlacement="start"
              sx={{
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                justifyContent: 'space-between',
                '& .MuiFormControlLabel-label': { flexGrow: 1 },
              }}
              control={
                <Switch
                  checked={settings.includeCoordinates}
                  onChange={(e): void => set({ includeCoordinates: e.target.checked })}
                  size="small"
                />
              }
              label={
                <Typography sx={{ color: gray80, fontSize: '13px' }}>
                  座標付与（表の列ズレ対策）
                </Typography>
              }
            />
            {/* temperature */}
            <Typography sx={{ color: gray80, fontSize: '13px', marginTop: '8px' }}>
              temperature: {settings.geminiTemperature}
            </Typography>
            <Slider
              value={settings.geminiTemperature}
              min={geminiSamplingParam.temperatureMin}
              max={geminiSamplingParam.temperatureMax}
              step={geminiSamplingParam.temperatureStep}
              valueLabelDisplay="auto"
              sx={sliderStyle}
              onChange={(_e, v): void => set({ geminiTemperature: Number(v) })}
            />
            {/* topP */}
            <Typography sx={{ color: gray80, fontSize: '13px', marginTop: '8px' }}>
              topP: {settings.geminiTopP}
            </Typography>
            <Slider
              value={settings.geminiTopP}
              min={geminiSamplingParam.topPMin}
              max={geminiSamplingParam.topPMax}
              step={geminiSamplingParam.topPStep}
              valueLabelDisplay="auto"
              sx={sliderStyle}
              onChange={(_e, v): void => set({ geminiTopP: Number(v) })}
            />
            {/* 思考量 */}
            <Typography sx={{ color: gray80, fontSize: '13px', marginTop: '8px' }}>
              思考量: {geminiThinkingSlider.labels[thinkingSliderValue] ?? '無効'}
              {isThinkingDisabled && '（このモデルは非対応）'}
            </Typography>
            <Slider
              value={thinkingSliderValue}
              min={geminiThinkingSlider.min}
              max={geminiThinkingSlider.max}
              step={1}
              marks
              disabled={isThinkingDisabled}
              valueLabelDisplay="off"
              sx={sliderStyle}
              onChange={(_e, v): void => {
                const { thinkingBudget, thinkingLevel } = sliderValueToThinking(
                  settings.geminiModel,
                  Number(v),
                );
                set({ thinkingBudget: thinkingBudget ?? 0, thinkingLevel });
              }}
            />
          </Box>
        </Box>

        {/* 構造化エンジン */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography sx={sectionLabel}>構造化エンジン</Typography>
          <CustomSelectField
            value={settings.structuredEngine}
            onChange={(e): void => set({ structuredEngine: Number(e.target.value) })}
            labelText=""
            requiredText=""
            list={[
              // 既定（structuredEngineType.gemini）は #3375 で 3.1 Flash-Lite に変更。
              // geminiFlashLite31 と同じモデルになるため、選択肢はこの1行に統合する。
              {
                option: 'Gemini 3.1 Flash-Lite（デフォルト）',
                description: getGeminiModelPriceLabel(geminiModel.flash_lite_3_1),
                value: structuredEngineType.gemini,
              },
              {
                option: 'Gemini 3.5 Flash-Lite',
                description: getGeminiModelPriceLabel(geminiModel.flash_lite_3_5),
                value: structuredEngineType.geminiFlashLite35,
              },
              {
                option: 'ChatGPT（GPT-4o）',
                description: GPT4O_PRICE_LABEL,
                value: structuredEngineType.chatgpt,
              },
            ]}
          />
        </Box>

        {/* 子パターンの選択（#2967）。子パターンを持つパターンでのみ出す */}
        {showLayoutSelect && (
          <Box sx={{ marginBottom: '24px' }}>
            <SubPatternReOcrSelect
              subPatterns={subPatterns}
              value={selectedLayout}
              onChange={setSelectedLayout}
            />
          </Box>
        )}

        {/* OCR枚数消費の案内（青文字・アップロード時と同じトーン） */}
        {creditInfoText && (
          <Typography sx={{ color: blue50, fontSize: '13px', marginBottom: '16px' }}>
            {creditInfoText}
          </Typography>
        )}

        {/* ボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button
            onClick={onClose}
            sx={{ ...tertiaryButtonBackgroundColor, color: gray80, border: `1px solid ${gray40}` }}
          >
            キャンセル
          </Button>
          <Button
            onClick={(): void => onExecute(showLayoutSelect ? selectedLayout : undefined)}
            sx={{ ...primaryButtonBackgroundColor, color: white }}
          >
            この設定で再読み取り
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReOcrSettingsModal;
